import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { recordTrackPlay, resolveFileUrl } from '../lib/api';

/**
 * Persistent audio player. Holds the current track + queue and a single
 * <audio> element so playback survives route navigation.
 *
 * API:
 *   const { current, queue, playing, play, pause, toggle, next, prev,
 *           playTrack, playQueue, enqueue, remove, seek, setVolume } = usePlayer();
 *
 * A track is: { id, title, file_url, cover_url, artist_name, artist_id, channel_id, duration_s }
 */

const PlayerCtx = createContext(null);

export function PlayerProvider({ children }) {
  const audioRef = useRef(null);
  const [queue, setQueue] = useState([]);     // upcoming tracks (excluding current)
  const [history, setHistory] = useState([]); // just-played
  const [current, setCurrent] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);   // 0..1
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolumeState] = useState(() => {
    const v = parseFloat(localStorage.getItem('rc-volume') || '0.9');
    return Number.isFinite(v) ? v : 0.9;
  });

  // Ensure one <audio> for lifetime of app
  useEffect(() => {
    const a = new Audio();
    a.preload = 'metadata';
    a.volume = volume;
    audioRef.current = a;

    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    const onTime = () => {
      setCurrentTime(a.currentTime || 0);
      setDuration(a.duration || 0);
      setProgress(a.duration ? a.currentTime / a.duration : 0);
    };
    const onEnded = () => { nextRef.current?.(); };
    const onError = () => { setPlaying(false); };

    a.addEventListener('play', onPlay);
    a.addEventListener('pause', onPause);
    a.addEventListener('timeupdate', onTime);
    a.addEventListener('loadedmetadata', onTime);
    a.addEventListener('ended', onEnded);
    a.addEventListener('error', onError);

    return () => {
      a.pause();
      a.removeEventListener('play', onPlay);
      a.removeEventListener('pause', onPause);
      a.removeEventListener('timeupdate', onTime);
      a.removeEventListener('loadedmetadata', onTime);
      a.removeEventListener('ended', onEnded);
      a.removeEventListener('error', onError);
    };
  }, []); // eslint-disable-line

  useEffect(() => {
    if (audioRef.current) audioRef.current.volume = volume;
    localStorage.setItem('rc-volume', String(volume));
  }, [volume]);

  const loadAndPlay = useCallback(async (track) => {
    const a = audioRef.current; if (!a || !track) return;
    const src = resolveFileUrl(track.file_url) || track.file_url;
    if (!src) return;
    a.src = src;
    try { await a.play(); setPlaying(true); recordTrackPlay(track.id); }
    catch { setPlaying(false); }
  }, []);

  const playTrack = useCallback((track) => {
    if (!track) return;
    setHistory(h => (current ? [current, ...h].slice(0, 50) : h));
    setCurrent(track);
    loadAndPlay(track);
  }, [current, loadAndPlay]);

  const playQueue = useCallback((tracks, startIdx = 0) => {
    if (!Array.isArray(tracks) || tracks.length === 0) return;
    const [first, ...rest] = tracks.slice(startIdx).concat(tracks.slice(0, startIdx));
    setQueue(rest);
    playTrack(first);
  }, [playTrack]);

  const enqueue = useCallback((track) => { setQueue(q => [...q, track]); }, []);
  const removeFromQueue = useCallback((idx) => setQueue(q => q.filter((_, i) => i !== idx)), []);
  const clearQueue = useCallback(() => setQueue([]), []);

  const play = useCallback(() => { audioRef.current?.play().catch(() => {}); }, []);
  const pause = useCallback(() => { audioRef.current?.pause(); }, []);
  const toggle = useCallback(() => { const a = audioRef.current; if (!a) return; a.paused ? a.play().catch(() => {}) : a.pause(); }, []);
  const seek = useCallback((frac) => { const a = audioRef.current; if (!a || !a.duration) return; a.currentTime = frac * a.duration; }, []);

  const next = useCallback(() => {
    if (queue.length === 0) { pause(); setPlaying(false); return; }
    const [n, ...rest] = queue;
    setQueue(rest);
    setHistory(h => (current ? [current, ...h].slice(0, 50) : h));
    setCurrent(n);
    loadAndPlay(n);
  }, [queue, current, pause, loadAndPlay]);
  const nextRef = useRef(next); useEffect(() => { nextRef.current = next; }, [next]);

  const prev = useCallback(() => {
    const a = audioRef.current;
    if (a && a.currentTime > 4) { a.currentTime = 0; return; }
    if (history.length === 0) { if (a) a.currentTime = 0; return; }
    const [p, ...rest] = history;
    setHistory(rest);
    if (current) setQueue(q => [current, ...q]);
    setCurrent(p);
    loadAndPlay(p);
  }, [history, current, loadAndPlay]);

  const stop = useCallback(() => { pause(); setCurrent(null); setQueue([]); }, [pause]);

  const setVolume = useCallback((v) => setVolumeState(Math.max(0, Math.min(1, v))), []);

  // Keyboard shortcut: space toggles when not in an input (and not on the immersive reel)
  useEffect(() => {
    const onKey = (e) => {
      const t = e.target;
      if (!current) return;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
      if (window.location.pathname === '/feed') return; // reel has its own controls
      if (e.code === 'Space') { e.preventDefault(); toggle(); }
      if (e.code === 'ArrowRight' && e.shiftKey) { e.preventDefault(); next(); }
      if (e.code === 'ArrowLeft' && e.shiftKey) { e.preventDefault(); prev(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [current, toggle, next, prev]);

  const value = useMemo(() => ({
    current, queue, history, playing, progress, currentTime, duration, volume,
    playTrack, playQueue, enqueue, removeFromQueue, clearQueue,
    play, pause, toggle, next, prev, seek, setVolume, stop,
  }), [current, queue, history, playing, progress, currentTime, duration, volume,
      playTrack, playQueue, enqueue, removeFromQueue, clearQueue, play, pause, toggle, next, prev, seek, setVolume, stop]);

  return <PlayerCtx.Provider value={value}>{children}</PlayerCtx.Provider>;
}

export const usePlayer = () => {
  const v = useContext(PlayerCtx);
  if (!v) throw new Error('usePlayer must be used inside <PlayerProvider>');
  return v;
};

// Small helper for UI: mm:ss
export const fmtTime = (s) => {
  if (!s || !Number.isFinite(s)) return '0:00';
  const mm = Math.floor(s / 60), ss = Math.floor(s % 60);
  return `${mm}:${String(ss).padStart(2, '0')}`;
};
